/**
 * CostCard — the "LLM cost" card body: period selection (Month | Quarter |
 * Custom + ‹ › stepper) driving CostBreakdownPanel.
 *
 * Default view (current month) renders straight from the dashboard payload —
 * no extra request on page load. Any other period fetches
 * /api/insights/cost/ via useCostSummary.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';

import { CostBreakdownPanel } from '@/components/CostBreakdownPanel';
import { useCostSummary } from '@/hooks/useCostSummary';
import {
  currentMonthPeriod,
  currentQuarterPeriod,
  customPeriod,
  isCurrentPeriod,
  stepPeriod,
  type Period,
  type PeriodMode,
} from '@/pages/insights/period';

import type { DashboardData } from '@/types/insights';

export interface CostCardProps {
  dashboard: DashboardData | null;
  isDashboardLoading: boolean;
}

const MONO = '"JetBrains Mono", monospace';

// The third element is the aria-label — omitted for "custom" because any
// aria-label matching /custom range/i necessarily contains the substring
// "to" (cus-TO-m), which collides with getByLabelText(/to/i) on the "To"
// date field once both render side by side. Using the visible label text
// ("Custom range") as the accessible name avoids an aria-label attribute
// altogether, so getByLabelText (which only inspects aria-label/<label>/
// aria-labelledby) no longer matches this button.
const MODE_LABELS: ReadonlyArray<[PeriodMode, string, string | undefined]> = [
  ['month', 'Month', 'By month'],
  ['quarter', 'Quarter', 'By quarter'],
  ['custom', 'Custom range', undefined],
];

function CostSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <Skeleton variant="text" width="40%" />
      <Skeleton variant="rounded" height={10} sx={{ my: 1 }} />
      <Skeleton variant="text" width="60%" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="text" width="90%" />
      ))}
    </Box>
  );
}

export function CostCard({ dashboard, isDashboardLoading }: CostCardProps) {
  const [mode, setMode] = useState<PeriodMode>('month');
  const [period, setPeriod] = useState<Period>(() => currentMonthPeriod());
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);

  // Custom mode is only "ready to fetch" once both dates are picked and the
  // range isn't inverted (to before from). Deriving this once keeps the
  // fetch gate and the empty/error messaging below in lockstep.
  const customValid =
    mode === 'custom' && customFrom !== null && customTo !== null && !customTo.isBefore(customFrom);

  // The default view — current month — renders from the dashboard payload.
  const isDefaultView = mode === 'month' && isCurrentPeriod(period);
  const fetchPeriod = isDefaultView || (mode === 'custom' && !customValid) ? null : period;
  const { data: fetched, isLoading, error, refetch } = useCostSummary(fetchPeriod);

  const view = useMemo(() => {
    if (isDefaultView) {
      return dashboard
        ? {
            costUsd: dashboard.cost_this_month_usd,
            budgetUsd: dashboard.cost_budget_usd,
            softLimitUsd: dashboard.cost_soft_limit_usd,
            breakdown: dashboard.cost_breakdown,
            label: period.label,
          }
        : null;
    }
    return fetched
      ? {
          costUsd: fetched.cost_usd,
          budgetUsd: fetched.budget_usd,
          softLimitUsd: fetched.soft_limit_usd,
          breakdown: fetched.cost_breakdown,
          label: period.label,
        }
      : null;
  }, [isDefaultView, dashboard, fetched, period.label]);

  const switchMode = (next: PeriodMode) => {
    setMode(next);
    if (next === 'month') setPeriod(currentMonthPeriod());
    if (next === 'quarter') setPeriod(currentQuarterPeriod());
    if (next === 'custom' && customFrom && customTo && !customTo.isBefore(customFrom)) {
      // Round-tripping back into custom mode with dates already picked (e.g.
      // custom -> month -> custom) must rebuild `period` from those dates —
      // otherwise the stale period left over from the other mode survives
      // and fetchPeriod below fetches/renders a period that doesn't match
      // what the pickers display.
      setPeriod(customPeriod(customFrom.format('YYYY-MM-DD'), customTo.format('YYYY-MM-DD')));
    }
    // custom with no (valid) dates picked yet: wait for both pickers before
    // building the period — the customValid gate keeps fetchPeriod null.
  };

  const onCustomChange = (from: Dayjs | null, to: Dayjs | null) => {
    setCustomFrom(from);
    setCustomTo(to);
    if (from && to && !to.isBefore(from)) {
      setPeriod(customPeriod(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')));
    }
  };

  const atCurrent = isCurrentPeriod(period);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        {/* Mode toggle — same idiom as the panel's Feature/Model toggle */}
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            overflow: 'hidden',
            bgcolor: 'background.deeper',
          }}
          role="group"
          aria-label="Cost period mode"
        >
          {MODE_LABELS.map(([key, label, aria]) => (
            <Box
              key={key}
              component="button"
              disabled={isLoading}
              onClick={() => {
                if (isLoading) return;
                switchMode(key);
              }}
              aria-pressed={mode === key}
              {...(aria ? { 'aria-label': aria } : {})}
              sx={{
                fontFamily: MONO,
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                px: 1.625,
                minHeight: 44,
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 140ms',
                ...(mode === key
                  ? { bgcolor: 'rgba(217,119,87,0.10)', color: '#D97757' }
                  : {
                      background: 'transparent',
                      color: 'text.disabled',
                      '&:hover': { color: 'text.primary' },
                    }),
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {mode !== 'custom' ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              size="large"
              aria-label="Previous period"
              disabled={isLoading}
              onClick={() => setPeriod((p) => stepPeriod(p, -1))}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              data-testid="cost-period-label"
              variant="body2"
              sx={{ fontFamily: MONO, minWidth: 76, textAlign: 'center' }}
            >
              {period.label}
            </Typography>
            <IconButton
              size="large"
              aria-label="Next period"
              disabled={atCurrent || isLoading}
              onClick={() => setPeriod((p) => stepPeriod(p, 1))}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={1}>
              <DatePicker
                label="From"
                value={customFrom}
                maxDate={customTo ?? dayjs()}
                onChange={(v) => onCustomChange(v, customTo)}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              <DatePicker
                label="To"
                value={customTo}
                minDate={customFrom ?? undefined}
                maxDate={dayjs()}
                onChange={(v) => onCustomChange(customFrom, v)}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
            </Stack>
          </LocalizationProvider>
        )}
      </Stack>

      {error && !isLoading ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load cost data: {error.message}
        </Alert>
      ) : mode === 'custom' && !customValid ? (
        <Typography variant="body2" color="text.secondary">
          {customFrom && customTo
            ? 'End date must be on or after the start date.'
            : 'Pick a start and end date to see the breakdown.'}
        </Typography>
      ) : isDashboardLoading || isLoading ? (
        <CostSkeleton />
      ) : view ? (
        <CostBreakdownPanel
          costUsd={view.costUsd}
          budgetUsd={view.budgetUsd}
          softLimitUsd={view.softLimitUsd}
          periodLabel={view.label}
          breakdown={view.breakdown}
        />
      ) : (
        <CostSkeleton />
      )}
    </Box>
  );
}
